import React, { useRef, useState } from "react";
import { Button, useDataProvider, useNotify } from "react-admin";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Dialog from "@mui/material/Dialog";
import Search from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useFormContext } from "react-hook-form";
import {
  buildItsLookupFilter,
  fetchNiyaazPreviousHistory,
  resolveHofLookupResults,
  type ItsdataHofRecord,
} from "./hof-lookup-utils";

export type { ItsdataHofRecord } from "./hof-lookup-utils";

export default function NiyaazHofLookup() {
  const notify = useNotify();
  const { setValue } = useFormContext();
  const [showDialog, setShowDialog] = useState(false);
  const dataProvider = useDataProvider();
  const [itsNo, setIts] = useState("");
  const [itsData, setData] = useState<ItsdataHofRecord[]>([]);
  const [noResult, setNoResult] = useState(false);
  const [loading, setLoading] = useState(false);
  const itsInputRef = useRef<HTMLInputElement>(null);

  const focusItsInput = () => {
    itsInputRef.current?.focus();
    itsInputRef.current?.select();
  };

  const resetDialog = () => {
    setIts("");
    setData([]);
    setNoResult(false);
    setLoading(false);
  };

  const handleClick = () => {
    resetDialog();
    setShowDialog(true);
  };

  const handleCloseClick = () => {
    setShowDialog(false);
    resetDialog();
  };

  const selectITS = (row: ItsdataHofRecord) => {
    setValue("HOFId", row.ITS_ID);
    setValue("HOFName", row.Full_Name);
    setValue("HOFAddress", row.Address);
    setValue("HOFPhone", row.Mobile);
    const family = (row.familyMembers || [])
      .sort((a, b) => b.HOF_FM_TYPE.localeCompare(a.HOF_FM_TYPE))
      .map((fam) => ({
        name: fam.Full_Name,
        age: fam.Age,
        gender: fam.Gender,
        its: fam.ITS_ID,
        hasRelay: false,
      }));
    setValue("familyMembers", family);

    fetchNiyaazPreviousHistory(row.ITS_ID)
      .then((previousHistory) => {
        if (previousHistory) {
          setValue("previousHistory", previousHistory);
        }
      })
      .catch((err: Error) => {
        notify(err.message, { type: "warning" });
      });

    handleCloseClick();
  };

  const searchITS = () => {
    const trimmed = itsNo.trim();
    if (!trimmed) {
      return;
    }

    setNoResult(false);
    setLoading(true);
    dataProvider
      .getList("itsdata", {
        pagination: { page: 1, perPage: 10 },
        sort: { field: "id", order: "ASC" },
        filter: buildItsLookupFilter(trimmed),
      })
      .then(({ data }) =>
        resolveHofLookupResults(dataProvider, data as ItsdataHofRecord[], trimmed)
      )
      .then((hofs) => {
        if (hofs.length === 0) {
          setNoResult(true);
          setData([]);
          return;
        }
        if (hofs.length === 1) {
          selectITS(hofs[0]);
          return;
        }
        setData(hofs);
      })
      .catch(() => {
        setNoResult(true);
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Button onClick={handleClick} label="ITS Lookup" variant="contained" size="small">
        <SearchIcon />
      </Button>
      <Dialog
        fullWidth
        open={showDialog}
        onClose={handleCloseClick}
        aria-label="Select itsData"
        TransitionProps={{ onEntered: focusItsInput }}
      >
        <DialogTitle>HOF / Family Member Lookup</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <TextField
              inputRef={itsInputRef}
              label="Enter HOF or Family Member ITS"
              value={itsNo}
              onChange={(e) => setIts(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => {
                if (e.key !== "Enter") {
                  return;
                }
                e.preventDefault();
                if (!loading && itsNo.trim()) {
                  searchITS();
                }
              }}
              inputMode="numeric"
              fullWidth
              sx={{ flex: 1, minWidth: 0, mr: 2, mt: 2 }}
            />
            <Search
              onClick={searchITS}
              variant="contained"
              sx={{ mt: 2, flexShrink: 0 }}
              disabled={loading}
            >
              {!loading ? <SearchIcon /> : <CircularProgress size={20} />}
            </Search>
          </Box>
          {noResult && <Typography sx={{ my: 3 }}>ITS not found in Jamaat</Typography>}
          {itsData.length > 0 && (
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>HOF Name</TableCell>
                  <TableCell>HOF ITS</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itsData.map((r) => (
                  <TableRow
                    key={r.id}
                    onClick={() => selectITS(r)}
                    hover
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{r.Full_Name}</TableCell>
                    <TableCell>{r.ITS_ID}</TableCell>
                    <TableCell>
                      <ArrowForwardIcon />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
